import { isLoaded } from 'react-redux-firebase';
import { createSelector } from 'reselect';
import { compact, map, orderBy } from 'lodash';

const companiesSelector = state => state.firestore.data.companies;
const companySelector = (state, id) => state.firestore.data.companies?.[id];
const companyIdSelector = (_state, id) => id;
const dealsSelector = state => state.firestore.data.deals;
const staffingSelector = state => state.staffing;

/**
 * Counts deals per companyId from the already-loaded staffing deals dump.
 * Used on Company cards so we do not fetch a second collection. Only deals that
 * store `companyId` are counted (name-only legacy rows are skipped).
 *
 * @param {Record<string, { companyId?: string }>|undefined} deals
 * @returns {Record<string, number>}
 */
const countDealsByCompanyId = deals => {
  const counts = {};

  map(deals, deal => {
    if (deal?.companyId) {
      counts[deal.companyId] = (counts[deal.companyId] || 0) + 1;
    }
  });

  return counts;
};

/**
 * Selector for the Companies list page: full docs, name-sorted, optional client-side search,
 * plus deal counts from the staffing deals listener.
 */
export const selectCompaniesList = () => {
  return createSelector(
    [companiesSelector, dealsSelector, staffingSelector],
    (companies, deals, staffing) => {
      if (!isLoaded(companies)) return { companies: [], isLoaded: false };

      const search = (staffing?.companiesSearch || '').trim().toLowerCase();
      const dealCounts = countDealsByCompanyId(deals);

      // react-redux-firebase keeps a deleted doc as `null` in `data.companies[id]`
      // until the key is dropped. Skip those entries — spreading/reading `.id` on
      // null is what crashed the list immediately after DELETE.
      const mapped = compact(
        map(companies, (company, companyId) => {
          if (!company) {
            return null;
          }

          // Use the Firestore map key, not company.id on the stored doc — some older
          // company records do not persist `id` on the document itself.
          return {
            ...company,
            id: company.id || companyId,
            dealCount: dealCounts[companyId] || 0,
          };
        }),
      );

      const filtered = search
        ? mapped.filter(company => (company.companyName || '').toLowerCase().includes(search))
        : mapped;

      return {
        companies: orderBy(filtered, ['companyName'], ['asc']),
        isLoaded: true,
        hasSearch: Boolean(search),
      };
    },
  );
};

/**
 * Selector for a single company document (create/edit form and card).
 * Deal count uses the already-loaded staffing deals dump so the card does not fetch extra data.
 */
export const selectCompany = () => {
  return createSelector(
    [companySelector, dealsSelector, companyIdSelector],
    (company, deals, companyId) => {
      if (!company) return null;

      const dealCounts = countDealsByCompanyId(deals);
      return {
        ...company,
        id: company.id || companyId,
        dealCount: dealCounts[companyId] || 0,
      };
    },
  );
};
